import { postInfoLogs } from '@/api/services/infoLogs';
import { deleteUser } from '@/api/services/user';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import { TextLink } from '@/components/TextLink';
import colors from '@/config/colors';
import { localLogoutUser } from '@/hooks/useAuth';
import { useDialog } from '@/hooks/useDialog';
import { PatientStackParamList } from '@/navigations/PatientStack';
import {
  Session,
  SessionsList,
} from '@/components/SharedComponents/SessionsList';
import { useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Divider } from 'react-native-elements';
import { ConfirmDeleteAccount } from './ConfirmDeleteAccount';
import { getSessions } from '@/stores/auth/services';
import constants from '@/config/constants';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

export const MyAccount = () => {
  const { navigate } =
    useNavigation<StackNavigationProp<PatientStackParamList>>();
  const { showDialog, removeDialog, removeAllDialogs } = useDialog();
  const { showNotification } = useNotification();
  const [activeSessions, setActiveSessions] = useState<Session[]>([]);

  const signedInUser = useStoreState((store) => store.signedInUser);
  const isDeleteEnabledRef = useRef<boolean>(false);

  const onChangePasswordPress = () => {
    removeDialog();
    navigate('PasswordChangeScreen');
  };

  const onEpaPress = () => {
    removeDialog();
    navigate('EpaScreen');
  };

  const onTextChange = (value: string): void => {
    isDeleteEnabledRef.current = false;
    if (value !== 'löschen') return;
    isDeleteEnabledRef.current = true;
  };

  const showConfirmDeleteDialog = () => {
    removeDialog();
    setTimeout(
      () =>
        showDialog({
          title: 'Löschen bestätigen',
          contentComponent: () => (
            <ConfirmDeleteAccount onTextChange={onTextChange} />
          ),
          confirmAction: {
            label: 'löschen',
            onPress: async () => {
              try {
                if (!isDeleteEnabledRef.current) return;
                await deleteUser();
                await localLogoutUser(true);
                removeAllDialogs();
              } catch (error) {
                logError('Error in showConfirmDeleteDialog', error);
                showNotification({
                  type: 'error',
                  title:
                    'Das Löschen des Kontos ist fehlgeschlagen. Bitte versuchen Sie es erneut',
                });
              }
            },
          },
          danger: true,
        }),
      100,
    );
  };

  const onDeleteAccountPress = () => {
    removeDialog();
    setTimeout(
      () =>
        showDialog(
          PREPARED_DIALOGS.accountDeleteDialog(showConfirmDeleteDialog),
        ),
      100,
    );
  };

  const onSendSupportDataPress = () => {
    postInfoLogs()
      .then(() => {
        removeAllDialogs();
        showNotification({
          type: 'success',
          title: 'Technische Informationen wurden gesendet',
        });
      })
      .catch((error) => {
        logError('Error in onSendSupportDataPress', error);
        showNotification({
          type: 'error',
          title: 'Das Senden der technischen Informationen ist fehlgeschlagen',
        });
      });
  };

  useEffect(() => {
    getSessions().then((res) => {
      setActiveSessions(res);
    });
  }, []);

  return (
    <View className="flex-1 w-full p-4">
      <View className="w-full flex-col justify-start bg-slate-50 p-2 rounded-md">
        <Text className="text-2xl">
          E-Mail-Adresse : {signedInUser?.user?.email ?? ''}
        </Text>
        <Text className="text-2xl">
          Patienten-Code : {signedInUser?.user?.code ?? ''}
        </Text>
      </View>
      <View className="m-3">
        <Text className="font-bold text-xl mb-2 ">Aktive Sitzungen:</Text>
        <SessionsList sessions={activeSessions} />
      </View>
      <Divider className="my-2" color={colors.grey} width={2} />

      {constants.epaEnabled ? (
        <View className="w-auto flex-col justify-start mb-2">
          <Pressable>
            <Text
              className="p-2 bg-green-neolexon rounded-md flex-row justify-center items-center"
              onPress={onEpaPress}
            >
              <Text className="text-white text-lg">
                Elektronische Patientenakte
              </Text>
            </Text>
          </Pressable>
        </View>
      ) : null}

      <View className="w-full flex-row justify-between">
        <Pressable
          className="p-2 bg-green-neolexon rounded-md mr-2 flex-row justify-center items-center"
          onPress={onChangePasswordPress}
        >
          <Icon name="password" className="text-white text-3xl mr-1" />
          <Text className="text-white text-lg">Passwort ändern</Text>
        </Pressable>
        <Pressable
          onPress={onDeleteAccountPress}
          className="p-2 bg-red-600 rounded-md flex-row justify-center items-center"
        >
          <Icon name="noAccounts" className="text-white text-3xl mr-1" />
          <Text className="text-white text-lg">
            Benutzerkonto und alle Daten löschen
          </Text>
        </Pressable>
      </View>
      <Divider className="my-4" color={colors.grey} width={2} />
      <View className="flex-col space-y-2">
        <Text className="text-lg font-bold">Geräteinformation senden</Text>
        <Text className="text-lg">
          Wenn ein Fehler in der App auftritt, melden Sie sich bitte bei unserem
          Support. Wir bitten Sie dann ggf. um Ihre Geräteinformationen gemäß
          unserer{' '}
          <TextLink href="https://neolexon.de/datenschutzerklaerung/">
            Datenschutzerklärung
          </TextLink>
          {'. '}
        </Text>
        <Pressable
          className="p-2 self-start bg-slate-100 rounded-md flex-row items-center active:border-green-700"
          onPress={onSendSupportDataPress}
        >
          {({ pressed }) => {
            return (
              <View className="flex-row items-center active:text-red-300 text-green-neolexon">
                <Icon
                  name="send"
                  className="text-green-neolexon pr-2 text-xl"
                />
                <Text
                  className={`${pressed ? 'text-green-neolexon' : ''} text-xl`}
                >
                  Geräteinformation senden
                </Text>
              </View>
            );
          }}
        </Pressable>
      </View>
    </View>
  );
};
